import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { fetchApi } from '../lib/api';
import type { InvoicingPreview, InvoicingRunResult, InvoicingReopenResult, ReopenPreview, AuditLogEntry } from '@repo/shared-types';
import { toast } from 'sonner';

/** Audit trail of month reopens (I6) — shown on the Chốt sổ page. */
export function useReopenHistory() {
  return useQuery<AuditLogEntry[]>({
    queryKey: ['invoicing', 'reopen-history'],
    queryFn: () => fetchApi('/invoicing/reopen-history'),
  });
}

export function useInvoicingPreview(month: string, enabled = true) {
  return useQuery<InvoicingPreview>({
    queryKey: ['invoicing', 'preview', month],
    queryFn: () => fetchApi(`/invoicing/preview?month=${month}`),
    enabled: enabled && /^\d{4}-\d{2}$/.test(month),
    // A month re-key (server-clock simulation, or the calendar rolling over)
    // swaps the queryKey; without this the query goes pending and flashes
    // loading UI (e.g. AttentionCard's skeleton) instead of keeping the last
    // month's preview on screen until the new one resolves.
    placeholderData: keepPreviousData,
  });
}

export function useRunInvoicing() {
  const qc = useQueryClient();
  return useMutation({
    // clientIds/dueDate omitted entirely when absent (every payer selected /
    // no override chosen), so the common case sends exactly the payload it
    // sent before THI-100's optional dueDate override (design spec §2's
    // "empty override → the +7 rule, unchanged" regression).
    mutationFn: ({ month, clientIds, dueDate }: { month: string; clientIds?: string[]; dueDate?: string }) =>
      fetchApi<InvoicingRunResult>('/invoicing/run', {
        method: 'POST',
        body: JSON.stringify({
          month,
          ...(clientIds !== undefined ? { clientIds } : {}),
          ...(dueDate !== undefined ? { dueDate } : {}),
        }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['invoices'] });
      qc.invalidateQueries({ queryKey: ['invoicing'] });
    },
    onError: (e: Error) => toast.error(e.message || 'Không thể tạo hoá đơn'),
  });
}

/** Rows for the partial-reopen picker (THI-170) — one per payer holding a
 *  BillingPeriod under this month, including invoice-less pure-credit payers. */
export function useReopenPreview(month: string, enabled = true) {
  return useQuery<ReopenPreview>({
    queryKey: ['invoicing', 'reopen-preview', month],
    queryFn: () => fetchApi(`/invoicing/reopen-preview?month=${month}`),
    enabled: enabled && /^\d{4}-\d{2}$/.test(month),
  });
}

export function useReopenMonth() {
  const qc = useQueryClient();
  return useMutation({
    // Row-level selection (fix-round 2): the picker ticks ROWS
    // (invoice-grained or credit-payer-grained — see ReopenPreviewRow), so
    // the wire must select at that same grain. `clientIds` (legacy,
    // payer-grained) stays supported for back-compat but the picker never
    // sends it — a payer-grained selection cannot tick one of a payer's two
    // invoices without also sweeping in the other (the second-close
    // scenario). Every field omitted entirely when absent/empty-by-default,
    // so a caller that only ever passes `month` still gets the full-reopen
    // reading.
    mutationFn: ({ month, clientIds, invoiceIds, creditClientIds }: {
      month: string; clientIds?: string[]; invoiceIds?: string[]; creditClientIds?: string[];
    }) =>
      fetchApi<InvoicingReopenResult>('/invoicing/reopen', {
        method: 'POST',
        body: JSON.stringify({
          month,
          ...(clientIds !== undefined ? { clientIds } : {}),
          ...(invoiceIds !== undefined ? { invoiceIds } : {}),
          ...(creditClientIds !== undefined ? { creditClientIds } : {}),
        }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['invoices'] });
      qc.invalidateQueries({ queryKey: ['invoicing'] });
    },
    onError: (e: Error) => toast.error(e.message || 'Không thể mở lại kỳ'),
  });
}
